import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-xl border-2 p-4 min-h-[60px] grid has-[>svg]:grid-cols-[calc(var(--spacing)*5)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-1 items-start [&>svg]:h-5 [&>svg]:w-5 [&>svg]:translate-y-0.5",
  {
    variants: {
      variant: {
        default: "bg-neutral-white border-neutral-200 text-foreground",
        destructive:
          "bg-error-100 border-error-300 text-foreground [&>svg]:text-error-700",
        success:
          "bg-success-100 border-success-300 text-foreground [&>svg]:text-success-700",
        warning:
          "bg-warning-100 border-warning-300 text-foreground [&>svg]:text-warning-700",
        info: "bg-info-100 border-info-300 text-foreground [&>svg]:text-info-700",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  children,
  ...props
}: Readonly<
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
      data-oid="mt1tr9_"
    >
      {children}
    </div>
  )
}

function AlertTitle({
  className,
  children,
  ...props
}: Readonly<React.HTMLAttributes<HTMLHeadingElement>>) {
  return (
    <h5
      data-slot="alert-title"
      className={cn(
        "col-start-2 font-semibold leading-none tracking-tight",
        className
      )}
      {...props}
      data-oid="9qpgzb4"
    >
      {children}
    </h5>
  )
}

function AlertDescription({
  className,
  children,
  ...props
}: Readonly<React.HTMLAttributes<HTMLParagraphElement>>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid min-w-0 justify-items-start gap-1 [&_p]:leading-relaxed text-foreground",
        className
      )}
      {...props}
      data-oid="nuiit85"
    >
      {children}
    </div>
  )
}

export { Alert, AlertTitle, AlertDescription }
export type { AlertProps }

type AlertProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants>
